import type { FC } from 'react';
import type { ScreenName } from 'components/Brief/Toolbar/helpers/OnScreen';
import type { MessageDescriptor } from 'react-intl';
import type { ODSOnDismissFunction } from 'bc-ods';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const registeredNotices = [
  'c2BoundaryResize',
  'cardLibraryDataFieldChosen',
  'columnsDropdown',
  'dataTableInlineEditingWarning',
  'dummyOnScreenNotice',
  'dummyReleaseNotesNotice',
  'dummyTipsAndTricksNotice',
  'favoritePlansForEasyAccess',
  'fitToViewBannerDismissed',
  'framesBannerDismissed',
  'mapClippingOnboarding',
  'planDashboardWelcomeBanner',
  'userNotifiedOfFileViewSwitchClick',
  'userNotifiedOfNoOneClick',
  'migratedFinalLists',
  'planSnapshots',
  'aiAssistButton',
  'aiAssistSidePaneSplashScreen',
  'presenterView',
  'templateBuilderImprovements',
  'knowledgeBaseNotice',
  'versionHistory',
  'cardLibrarySearchOptions',
  'commenterRole',
  'viewMenuCommentMode',
] as const;

type RegisteredNoticeKey = (typeof registeredNotices)[number];
type NoticeRegistry = Record<RegisteredNoticeKey, RegisteredNotice>;

enum NoticeTypeEnum {
  ON_SCREEN = 'onScreen',
  RELEASE_NOTES = 'releaseNotes',
  MANUAL_TRIGGER = 'manualTrigger',
  TIPS_AND_TRICKS = 'tipsAndTricks',
}

type ReleaseNotesSection = {
  /**
   * A `react-intl` message representing the header of the release notes section.
   */
  header: MessageDescriptor;
  /**
   * The items in the release notes section. These are `react-intl` messages and will be displayed as a bulleted list.
   */
  items: MessageDescriptor[];
};

type RegisteredNoticeBase = {
  /**
   * Whether this notice should always be shown when eligible, regardless of whether any other notices are visible.
   * Generally not needed, but can be useful if you have a non-standard notice.
   */
  alwaysShow?: boolean;
  /*
   * The associated component to render for this notice. This can be used in lieu of the `message` and `image` fields
   * for all notice types except `MANUAL_TRIGGER` notices.
   */
  Component?: FC;
  /**
   * Used for `RELEASE_NOTES`. A `react-intl` message representing the description of the release notes.
   * This is typically a high-level overview of the changes, and the sections will provide more detail.
   */
  description?: MessageDescriptor;
  /**
   * Used for `RELEASE_NOTES`. Link to the full release notes PDF.
   */
  link?: string;
  /**
   * The image to display in the notice. This is optional, as not all notices need an associated image.
   * This property does nothing if a custom `Component` is provided.
   */
  image?: string;
  /*
   * Whether this notice is a debug notice. Debug notices are only displayed after turning them on in the debug menu.
   */
  isDebugNotice?: boolean;
  /**
   * The message to display in the notice. This is not required if a custom `Component` is provided.
   */
  message?: MessageDescriptor;
  /*
   * The type of notice to render. This will determine how the notice is displayed and how it is dismissed.
   * - `MANUAL_TRIGGER` - not automatically displayed and must be rendered manually.
   * - `ON_SCREEN` - displayed when the user navigates to a type of top-level artifact.
   * - `RELEASE_NOTES` - displayed when the user logs in after a new release.
   * - `TIPS_AND_TRICKS` - displayed when the user logs in.
   */
  noticeType: NoticeTypeEnum;
  /*
   * The type of page you want this notice to show on. This is used for `ON_SCREEN` notices.
   */
  screen?: ScreenName;
  /**
   * Used for `RELEASE_NOTES`. The sections of the release notes, which contain `react-intl` messages
   * representing headers and bullet points.
   * @example
   * Assuming the `messages` object is populated with the `defineMessages` method from `react-intl`,
   * you can structure the sections like so:
   * ```typescript
   * [{
   *   header: messages.section1Header,
   *   items: [
   *     messages.section1Item1,
   *     messages.section1Item2,
   *     messages.section1Item3
   *   ],
   * }]
   * ```
   */
  sections?: ReleaseNotesSection[];
  /*
   * The title of the notice. This is used for `ON_SCREEN` notices, and is not required if a custom `Component` is provided.
   */
  title?: MessageDescriptor;
  /*
   * The version this notice was introduced in. This is used for `RELEASE_NOTES` notices.
   */
  version?: string;
};

type WithProps<P extends keyof RegisteredNoticeBase> = Required<Pick<RegisteredNoticeBase, P>>;

type ManualTriggerNoticeType = { noticeType: NoticeTypeEnum.MANUAL_TRIGGER };
type OnScreenNoticeType = (
  | WithProps<'Component' | 'screen'>
  | WithProps<'message' | 'screen' | 'title'>
) & {
  noticeType: NoticeTypeEnum.ON_SCREEN;
};
type ReleaseNotesNoticeType = WithProps<'description' | 'link' | 'sections' | 'version'> & {
  noticeType: NoticeTypeEnum.RELEASE_NOTES;
};
type TipsAndTricksNoticeType = (WithProps<'Component'> | WithProps<'message'>) & {
  noticeType: NoticeTypeEnum.TIPS_AND_TRICKS;
};

type RegisteredNoticeByType =
  | ManualTriggerNoticeType
  | OnScreenNoticeType
  | ReleaseNotesNoticeType
  | TipsAndTricksNoticeType;

type RegisteredNotice = RegisteredNoticeBase & RegisteredNoticeByType;

type UseDismissibleNotice = (key: RegisteredNoticeKey) => {
  dismiss: ODSOnDismissFunction;
  snooze: () => void;
  visible: boolean;
};

type UseResetDismissibleNotices = () => {
  resetNoticesByType: (noticeType: NoticeTypeEnum) => Promise<boolean>;
};

type NoticeTypePreferences = {
  /**
   * Whether the notice type is muted or not.
   */
  muted: boolean;
};

type UserNoticePreferences = {
  [key in NoticeTypeEnum]?: NoticeTypePreferences;
};

type SnoozedNotices = {
  [key in RegisteredNoticeKey]?: Date;
};

export { NoticeTypeEnum };
export type {
  ManualTriggerNoticeType,
  NoticeRegistry,
  NoticeTypePreferences,
  OnScreenNoticeType,
  RegisteredNotice,
  RegisteredNoticeKey,
  ReleaseNotesNoticeType,
  ReleaseNotesSection,
  SnoozedNotices,
  TipsAndTricksNoticeType,
  UseDismissibleNotice,
  UseResetDismissibleNotices,
  UserNoticePreferences,
};
